<script setup lang="ts">
import type { GradeCode } from '~/models/Grade'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { Settings } from 'luxon'
import { useNetwork } from '@vueuse/core'
import { useRoute, useRouter } from '#imports'
import { gradesSorted } from '~/utils/gradeSorter'
import { FOCUSABLE } from '~/utils/dom'
import useProductStore from '~/stores/product'
import { useMessagesStore } from '~/stores/messages'
import useLicenseControlStore from '~/stores/licenseControl'
import useGroupsStore from '~/stores/groups'
import useFilterStore from '~/stores/filter'
import { useAuthStore } from '~/stores/auth'
import { PreferredLanguage } from '~/models/User/PreferredLanguage'
import { TeleportationTarget } from '~/models/TeleportationTarget'
import { useRouteMeta } from '~/composables/useRouteMeta'
import { useAppColor } from '~/composables/useAppColor'
import useAppBanner from '~/composables/banner'
import AuToast from '~/components/ui/AuToast.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import PreviewOverlay from '~/components/paywall/PreviewOverlay.vue'
import PaywallOverlay from '~/components/paywall/PaywallOverlay.vue'
import WizardDialog from '~/components/onboarding/WizardDialog.vue'
import { keyboardIsVisible } from '~/components/chat/mathLiveKeyboardState'
import AppHeader from '~/components/AppHeader.vue'
import AppFooter from '~/components/AppFooter.vue'
import '~/styles/index.css'

const route = useRoute()
const router = useRouter()
const { getProducts } = useProductStore()
const { loadGroups } = useGroupsStore()
const { getMessages } = useMessagesStore()
const { locale, t } = useI18n()
const { banner } = useAppBanner()
const { hideAppFooter, hideAppHeader } = useRouteMeta()
const { bgColor, themeColor } = useAppColor()
const { hasAccessRestricted } = storeToRefs(useLicenseControlStore())
const { selectedGrade } = storeToRefs(useFilterStore())
const {
  userPreferredLanguage,
  userPreferredGrade,
  userRelevantGrades,
  userOrganization,
  isAuthenticated,
  hasLoadedUser,
  needsOrganization,
} = storeToRefs(useAuthStore())
const { isOnline } = useNetwork()

const focusFirstFocusableElement = () => {
  const appContent = document.querySelector('#app-content')
  if (!appContent)
    return
  const firstFocusableElement = appContent.querySelectorAll(FOCUSABLE).item(0)
  if (!firstFocusableElement)
    return
  (firstFocusableElement as HTMLElement).focus()
}

const appContentClasses = computed(() => [
  { 'pb-[370px]': keyboardIsVisible.value }, // Adding padding to article view when math live keyboard is visible, preventing blocking of the chatbot input
  'before:content-[\'\'] before:fixed before:top-0 before:-z-10 before:h-44 before:inset-x-0 before:bg-(--app-theme)',
  ...(hasAccessRestricted.value ? ['max-h-screen', 'overflow-hidden'] : []),
])

const hasPreviewOverlay = computed(() => {
  const hasPreview = !!router.currentRoute.value?.meta?.preview
  return hasPreview && hasLoadedUser.value && !isAuthenticated.value
})

onMounted(() => getMessages())

watch(userPreferredLanguage, (newPreferredLanguage) => {
  document.documentElement.lang = newPreferredLanguage
  locale.value = newPreferredLanguage
  Settings.defaultLocale = userPreferredLanguage.value || PreferredLanguage.Bokmal
}, { immediate: true })

watch([hasLoadedUser, userOrganization], (newData, oldData) => {
  if (
    isAuthenticated.value
    && !needsOrganization.value
    && JSON.stringify(newData) !== JSON.stringify(oldData)
  ) {
    getProducts()
    loadGroups()
  }
}, { immediate: true })

watch([hasLoadedUser, route], async () => {
  await router.isReady()
  const gradeParam = router.currentRoute.value?.query?.grade as GradeCode | undefined
  const validGrade = gradeParam && gradesSorted.includes(gradeParam) ? gradeParam : false
  selectedGrade.value = validGrade || userPreferredGrade.value || userRelevantGrades.value[0]
}, { deep: true, flush: 'pre', immediate: true })
</script>

<template>
  <a
    v-if="banner"
    href="#app-content"
    tabindex="1"
    class="bg-seagreen-40 fixed z-50 -translate-x-full -translate-y-full rounded-lg p-2 font-medium whitespace-pre-line text-white focus:translate-x-4 focus:translate-y-4 focus:ring focus:ring-offset-2"
    @click.prevent.stop="focusFirstFocusableElement"
    v-text="t('jumpToContent')"
  />
  <AppHeader v-if="!hideAppHeader" />
  <div :class="TeleportationTarget.AppTop.replace('.', '')" />
  <main
    id="app-content"
    class="mx-auto w-full grow bg-(--app-bg)"
    :class="appContentClasses"
    :style="{
      '--app-theme': themeColor,
      '--app-bg': bgColor,
    }"
  >
    <AuCallout
      v-if="!isOnline"
      variant="error"
      class="!z-100"
      :title="t('offline')"
      dismissible
    />
    <PreviewOverlay v-if="hasPreviewOverlay" />
    <PaywallOverlay v-else-if="hasAccessRestricted" />
    <NuxtPage />
  </main>
  <AppFooter v-if="!hideAppFooter" />
  <AuToast />
  <WizardDialog />
</template>
